import { useState } from 'react'
import { useBoolean, useInterval, useMount } from 'react-use'
import type { DbRestoreStatus } from '@ztechapps/common.contract'
import { DbRestoreStateEnum } from '@ztechapps/common.contract'
import { useFetch } from '../useFetch/index'

const POLL_INTERVAL_MS = 3000

export type UseDbRestoreStatusResult = {
  status?: DbRestoreStatus
  /** True from the first render until the first status request settles — only while `enabled`. */
  loading: boolean
  running: boolean
  failed: boolean
  succeeded: boolean
  /** The API answered Idle after this tab saw a restore running — the process restarted mid-restore. */
  interrupted: boolean
  /** Hands the hook the status the restore POST answered with, so it starts polling at once. */
  onStarted: (status: DbRestoreStatus) => void
}

/**
 * The API's restore status, polled every 3 seconds while a restore runs.
 * Fetches once on mount when `enabled`; a finished restore found on that first load is not reported
 * as `failed`, `succeeded` or `interrupted` — only a restore this tab watched run is.
 */
export const useDbRestoreStatus = ({ enabled = false }: { enabled?: boolean } = {}): UseDbRestoreStatusResult => {
  // `raw`: a Failed status body carries `error`, which useFetch would otherwise rethrow as a failed request.
  const [fetchNow] = useFetch('/api/restore-db/status', { method: 'GET' }, { raw: true })
  const [status, setStatus] = useState<DbRestoreStatus>()
  const [settled, setSettled] = useBoolean(false)
  const [watching, setWatching] = useBoolean(false)

  const refresh = async () => {
    const response = await fetchNow()
    const result: DbRestoreStatus | undefined = response?.ok ? await response.json().catch(() => undefined) : undefined

    // A failed request — a network error, a non-2xx reply or a body that is not JSON —
    // keeps the last status and keeps polling.
    if (result?.state) {
      setStatus(result)
      if (result.state === DbRestoreStateEnum.Running) setWatching(true)
    }
  }

  const onStarted = (started: DbRestoreStatus) => {
    setStatus(started)
    setWatching(true)
  }

  useMount(() => {
    if (enabled) refresh().then(() => setSettled(true))
  })

  useInterval(refresh, status?.state === DbRestoreStateEnum.Running ? POLL_INTERVAL_MS : null)

  const isWatching = (state: DbRestoreStateEnum) => watching && status?.state === state

  return {
    status,
    loading: enabled && !settled,
    running: status?.state === DbRestoreStateEnum.Running,
    failed: isWatching(DbRestoreStateEnum.Failed),
    succeeded: isWatching(DbRestoreStateEnum.Succeeded),
    interrupted: isWatching(DbRestoreStateEnum.Idle),
    onStarted,
  }
}
